import React from 'react';

import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import { OptionData } from '@atlaskit/smart-user-picker';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

import { BlockerIcon } from 'src/components/icon';
import getReviewers from 'src/components/reviewers/src/get-reviewers';
import { ParticipantStateTypes } from 'src/components/types';
import { useCheckUserAccess } from 'src/hooks/use-check-user-access';
import { ADD_REVIEWER, REMOVE_REVIEWER } from 'src/redux/pull-request/actions';
import {
  getCurrentPullRequest,
  getCurrentPullRequestAuthor,
  getCurrentPullRequestUrlPieces,
  getIsCurrentPullRequestChangesRequested,
  getIsCurrentPullRequestOpen,
  getPullRequestApprovalsMergeCheck,
} from 'src/redux/pull-request/selectors';
import { getCurrentUserKey } from 'src/selectors/user-selectors';

import { GrayIcon } from './gray-icon';
import ReviewerStatusesCard from './reviewer-statuses-card';

type PrReviewerStatusesCardProps = {
  isSidebarCollapsed?: boolean;
};

export const PullRequestReviewerStatusesCard = ({
  isSidebarCollapsed,
}: PrReviewerStatusesCardProps) => {
  const pullRequest = useSelector(getCurrentPullRequest, shallowEqual);
  const currentUserUuid = useSelector(getCurrentUserKey);
  const dispatch = useDispatch();
  const isOpenPr = useSelector(getIsCurrentPullRequestOpen);
  const canAddReviewers = useCheckUserAccess('write') && isOpenPr;
  const isChangesRequested = useSelector(
    getIsCurrentPullRequestChangesRequested
  );
  const pullRequestApprovalsMergeCheck = useSelector(
    getPullRequestApprovalsMergeCheck
  );
  const currentPullRequestAuthor = useSelector(getCurrentPullRequestAuthor);
  const { owner: workspace_slug, slug: repo_slug } = useSelector(
    getCurrentPullRequestUrlPieces
  );

  if (!pullRequest) return null;
  const reviewers = getReviewers(pullRequest, currentUserUuid);
  const onSelection = (user: OptionData) => {
    dispatch({
      type: ADD_REVIEWER.REQUEST,

      payload: {
        userAaid: user.id,
      },
    });
  };

  const onDelete = (userId: string) => {
    dispatch({
      type: REMOVE_REVIEWER.REQUEST,

      payload: {
        userAaid: userId,
      },
    });
  };

  const approvalsCount = reviewers.filter(
    participant => participant.state === ParticipantStateTypes.approved
  ).length;
  const pullRequestApprovalsMergeCheckCount =
    pullRequestApprovalsMergeCheck?.count || 0;

  const renderSecondaryIcon = () => {
    if (isChangesRequested) {
      return (
        <BlockerIcon
          primaryColor={token('color.icon.warning', colors.Y300)}
          label=""
        />
      );
    } else if (
      approvalsCount !== 0 &&
      approvalsCount >= pullRequestApprovalsMergeCheckCount
    ) {
      return (
        <CheckCircleIcon
          size="medium"
          primaryColor={token('color.icon.success', colors.G300)}
          label=""
        />
      );
    }
    return <GrayIcon />;
  };

  const fullSlug = `${workspace_slug}/${repo_slug}`;

  return (
    <ReviewerStatusesCard
      isSidebarCollapsed={isSidebarCollapsed}
      onSelection={onSelection}
      onDelete={onDelete}
      reviewers={reviewers}
      canAddReviewers={canAddReviewers}
      renderSecondaryIcon={renderSecondaryIcon}
      repoFullSlug={fullSlug}
      currentUser={currentPullRequestAuthor}
      isDraft={!!pullRequest.draft}
    />
  );
};
